/**
 * Meybra Interactive Demo — Public Landing Page
 *
 * The single public entry point for prospects (route `/demo`). Collects
 * lightweight visitor info (name, email, org, country, role) and hands off
 * to the correct experience track (Club / Private Coach / Athlete-Parent).
 *
 * The visitor's role selection maps to one of three internal "tracks" —
 * everyone in a club-facing role lands in the Club Experience with the full
 * panel switcher available, private coaches get a leaner two-panel view,
 * and families are dropped straight into the Athlete/Parent perspective.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowRight, Sparkles, Users, Building2, User, Zap, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDemo } from '@/context/DemoContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { value: 'club_owner', label: 'Club Owner / Director', icon: Building2, track: 'club' },
  { value: 'team_coach', label: 'Team Coach', icon: Users, track: 'club' },
  { value: 'team_manager', label: 'Team Manager', icon: Users, track: 'club' },
  { value: 'private_soccer_coach', label: 'Private Soccer Coach', icon: Zap, track: 'private' },
  { value: 'private_performance_coach', label: 'Private Performance Coach', icon: Zap, track: 'private' },
  { value: 'parent_athlete', label: 'Parent / Athlete', icon: Heart, track: 'family' },
];

const DemoLandingPage = () => {
  const navigate = useNavigate();
  const { setSessionId, refresh, seedRoleStorage, wipeRoleStorage } = useDemo();
  const [form, setForm] = useState({ name: '', email: '', organization: '', country: '', role: '' });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit = form.name.trim().length >= 2 && /.+@.+\..+/.test(form.email) && form.role;

  const start = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/demo/start`, {
        name: form.name.trim(),
        email: form.email.trim(),
        organization: form.organization.trim() || null,
        country: form.country.trim() || null,
        role: form.role,
      });
      // Wipe any leftover login keys, then seed the exact keys the
      // impersonated role's layout guard expects.
      if (wipeRoleStorage) wipeRoleStorage();
      if (seedRoleStorage) seedRoleStorage(res.data.token, res.data.user);
      setSessionId(res.data.session_id);
      await refresh(res.data.session_id);
      // Force a hard navigation so AuthProvider hydrates from the new token.
      window.location.href = res.data.redirect || '/';
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not start the demo. Please try again.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="demo-landing">
      <header className="border-b border-border/40">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="font-heading text-lg tracking-widest uppercase">
            Meybra
          </button>
          <span className="text-[10px] uppercase tracking-widest text-primary font-bold px-3 py-1 border border-primary/40 rounded-full">
            Interactive Demo
          </span>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-6 py-10 sm:py-16 grid lg:grid-cols-2 gap-10 items-start">
          {/* Left — Value prop */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/5 text-primary text-[11px] uppercase tracking-widest">
              <Sparkles size={12} /> No signup required
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase leading-none">
              Step inside Meybra.<br />
              <span className="text-primary">See the full platform</span> — as if it were yours.
            </h1>
            <p className="text-muted-foreground text-base leading-relaxed max-w-lg">
              Explore Meybra using a live club with real teams, athletes,
              evaluations and AI-generated insights. Switch between the Club
              Dashboard, Team Coach, Team Manager and Athlete/Parent
              experiences — every write happens in an isolated sandbox that
              resets after 24 hours.
            </p>
            <ul className="space-y-3 text-sm">
              {[
                'Real dataset: 37 teams, 100+ athletes, months of history',
                'Try the AI Development Assistant, Quick Update and Weekly Challenges',
                'See the Unified Development Profile across Club + Private evaluations',
                'Zero commitment — leave whenever you want',
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-muted-foreground">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right — Form */}
          <div className="bg-card border border-border/60 rounded-lg p-6 sm:p-8 space-y-5" data-testid="demo-form">
            <div>
              <p className="font-heading text-lg uppercase tracking-widest">Start your demo</p>
              <p className="text-xs text-muted-foreground mt-1">Takes ~10 seconds. No credit card.</p>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="demo-name" className="text-xs uppercase tracking-widest text-muted-foreground">Full name</Label>
                <Input id="demo-name" value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Alex Rivera" data-testid="demo-input-name" />
              </div>
              <div>
                <Label htmlFor="demo-email" className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
                <Input id="demo-email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="alex@yourclub.com" data-testid="demo-input-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="demo-org" className="text-xs uppercase tracking-widest text-muted-foreground">Club / Org</Label>
                  <Input id="demo-org" value={form.organization} onChange={(e) => update('organization', e.target.value)} placeholder="Your club" data-testid="demo-input-org" />
                </div>
                <div>
                  <Label htmlFor="demo-country" className="text-xs uppercase tracking-widest text-muted-foreground">Country</Label>
                  <Input id="demo-country" value={form.country} onChange={(e) => update('country', e.target.value)} placeholder="Brazil" data-testid="demo-input-country" />
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Which best describes you?</p>
              <div className="grid gap-2">
                {ROLES.map((r) => {
                  const Icon = r.icon;
                  const active = form.role === r.value;
                  return (
                    <button
                      type="button"
                      key={r.value}
                      onClick={() => update('role', r.value)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-sm border text-left transition-colors ${
                        active ? 'border-primary bg-primary/10' : 'border-border/40 hover:border-border/70'
                      }`}
                      data-testid={`demo-role-${r.value}`}
                    >
                      <span className={`w-8 h-8 rounded-sm flex items-center justify-center ${active ? 'bg-primary text-black' : 'bg-muted/50 text-muted-foreground'}`}>
                        <Icon size={14} />
                      </span>
                      <span className="text-sm">{r.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <Button
              type="button"
              onClick={start}
              disabled={!canSubmit || submitting}
              className="w-full bg-primary text-black hover:bg-primary/90 uppercase tracking-widest font-bold h-12"
              data-testid="demo-start-btn"
            >
              {submitting ? (
                <><Loader2 className="mr-2 animate-spin" size={16} /> Preparing your sandbox…</>
              ) : (
                <>Start Interactive Experience <ArrowRight className="ml-2" size={16} /></>
              )}
            </Button>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              By continuing you agree to receive occasional updates about Meybra.
              Your sandbox is isolated — nothing you do here affects real users.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DemoLandingPage;
